import { Button } from "@mui/joy";
import { useEffect, useState } from "react";
import {
  getMultiPlanPrices,
  getSinglePlanPrice,
  PlanKey,
  planKeyToName,
} from "../../components/task/payment/planTypes";
import {
  loadTossPayments,
  TossPaymentsPayment,
} from "@tosspayments/tosspayments-sdk";
import { v4 as uuidv4 } from "uuid";
import { trackCapiStandardEvent } from "analytics/metaCapi";
import { trackXAdsEvent } from "analytics/xAdsPixel";
import { isAdTrackingSuppressed } from "analytics/adTracking";
import useMixpanelMKT from "../../hooks/useMixpanelMKT";
import { userSimpleAtom } from "../../recoil/userSimpleAtom";
import { useRecoilState } from "recoil";
import useAutoLoginPromotion from "../../hooks/useAutoLoginPromotion";
import { useNavigate } from "react-router-dom";
import { getFunnelPaths } from "routes/adFunnel";
import useGtagConversion from "../../hooks/useGtagConversion";
import { useMutation } from "@tanstack/react-query";
import { webFunnelApi } from "../../api/webFunnelApi";
import { userApi } from "../../api/userApi";

export default function PaymentButtonPromotion({
  selectedPlan,
}: {
  selectedPlan: PlanKey;
}) {
  const tossPaymentsClientKey = process.env.REACT_APP_TOSSPAYMENTS_CLIENT_KEY!!;

  const [user, setUser] = useRecoilState(userSimpleAtom);
  const { isChecking } = useAutoLoginPromotion();
  const navigate = useNavigate();
  const [tossPaymentsPayment, setTossPaymentsPayment] =
    useState<TossPaymentsPayment | null>();
  // 결제창을 여는 동안 버튼을 잠근다. 연타·이중 결제 방어의 1차선.
  const [isRequesting, setIsRequesting] = useState(false);

  const trackGtagEvent = useGtagConversion();

  const { mutate: trackEvent } = useMutation(
    ({
      eventName,
      properties,
    }: {
      eventName: string;
      properties?: { [key: string]: any };
    }) => webFunnelApi.trackEvent(eventName, properties)
  );

  const trackPromotion = useMixpanelMKT({
    eventName: "결제하기 버튼 클릭",
    version: "v4",
  });

  const buttonText = "결제하기";

  function computeAmountValue(): number {
    const pricingModel = user?.pricingModel;

    // e/h/i: 단일 "맞춤 플랜"이 challenger로 매핑되며 모델별 가격 오버라이드
    const singlePlanPrice = getSinglePlanPrice(pricingModel);
    if (singlePlanPrice !== undefined && selectedPlan === "challenger") {
      return singlePlanPrice;
    }

    // aeg: 다중 플랜 전체 가격 오버라이드
    const multiPlanPrices = getMultiPlanPrices(pricingModel);
    if (multiPlanPrices) {
      return multiPlanPrices[selectedPlan] ?? multiPlanPrices.challenger;
    }

    // 기본 가격
    if (selectedPlan === "starter") return 295000;
    if (selectedPlan === "challenger") return 395000;
    if (selectedPlan === "master") return 465000;
    return 395000;
  }

  /**
   * 결제창을 열기 직전에 구독 상태를 서버에서 다시 확인한다.
   *
   * 버튼의 `user?.isSubscribing` 은 로그인 시점에 한 번 받아 둔 값이다 — useAutoLoginPromotion 은
   * accessToken 이 이미 있으면 다시 받아오지 않는다. 그래서 결제 완료 후 뒤로가기(bfcache 복원 포함)로
   * 이 페이지에 돌아오면 여전히 false 인 채 버튼이 열려 있다. 실제 이중 결제가 이 경로로 났다.
   *
   * 조회 자체가 실패하면 결제를 막지 않는다 — 서버의 중복 결제 가드가 승인 전에 다시 본다.
   */
  async function isAlreadySubscribing(): Promise<boolean> {
    try {
      const response = await userApi.getSimplePromotion();
      if (response.code !== 0 || !response.result) return false;

      setUser((prev) => ({ ...prev, ...response.result }));
      return response.result.isSubscribing === true;
    } catch (e) {
      return false;
    }
  }

  async function requestTossPayments() {
    if (tossPaymentsPayment) {
      try {
        // PaymentFailPromotion 에서 Toss 도메인 항목을 정확히 건너뛰기 위해
        // 결제 시작 직전 history.length 를 저장한다.
        try {
          sessionStorage.setItem(
            "promotionPaymentStartHistoryLength",
            String(window.history.length)
          );
        } catch {}

        await tossPaymentsPayment?.requestPayment({
          method: "CARD",
          amount: {
            currency: "KRW",
            value: computeAmountValue(),
          },
          orderId: uuidv4(),
          orderName: `디스턴싱 ${planKeyToName[selectedPlan]} 플랜 / 100일`,
          customerEmail: user?.email || "",
          customerName: user?.nickname,
          // 결제 후 복귀 URL 은 현재 퍼널 prefix(/promotion, /gads)를 따른다
          successUrl: window.location.origin + getFunnelPaths().tossSuccess,
          failUrl: window.location.origin + getFunnelPaths().tossFail,
          card: {
            useEscrow: false,
            flowMode: "DEFAULT",
            useCardPoint: false,
            useAppCardOnly: false,
          },
        });
      } catch (error) {
        // 에러 처리하기
        console.error(error);
      }
    }
  }

  useEffect(() => {
    async function fetchTossPaymentsPayment() {
      if (tossPaymentsClientKey) {
        const tossPayments = await loadTossPayments(tossPaymentsClientKey);
        const payment = tossPayments.payment({
          customerKey: `ck_${uuidv4()}`,
        });

        setTossPaymentsPayment(payment);
      }
    }

    fetchTossPaymentsPayment();
  }, [tossPaymentsClientKey]);

  // 로그인 체크 후 리다이렉트
  useEffect(() => {
    if (!isChecking && !user?.accessToken) {
      navigate(getFunnelPaths().login, { replace: true });
    }
  }, [isChecking, user, navigate]);

  const handlePaymentRequest = async () => {
    if (isRequesting) return;
    setIsRequesting(true);

    if (await isAlreadySubscribing()) {
      setIsRequesting(false);
      alert("이미 결제가 완료된 계정이에요. 앱에서 프로그램을 시작해 주세요.");
      navigate(getFunnelPaths().completed, { replace: true });
      return;
    }

    // 광고 전환(Meta CAPI·Google Ads·X Ads)은 내부자·제휴 프로모션 유저 제외 — 메인 앱과 같은 기준. Mixpanel 은 항상 전송
    if (!isAdTrackingSuppressed(user)) {
      trackCapiStandardEvent("InitiateCheckout", {
        externalId: user?.userId,
        email: user?.email,
      });
      trackGtagEvent("InitiateCheckout", "v4", {
        email: user?.email,
        sha256EmailAddress: user?.sha256EmailAddress,
        sha256PhoneNumber: user?.sha256PhoneNumber,
        user,
      });
      trackXAdsEvent("AddPaymentInfo", {
        email: user?.email,
      });
    }
    trackPromotion();

    trackEvent({
      eventName: "결제 버튼 클릭",
      properties: {
        event_source: "web",
      },
    });

    await requestTossPayments();

    // 결제창이 닫혀(취소·실패) 이 페이지로 돌아온 경우에만 여기까지 온다. 성공이면 successUrl 로 떠난다.
    setIsRequesting(false);
  };

  return (
    <>
      <Button
        loading={isChecking || isRequesting}
        disabled={
          isChecking ||
          isRequesting ||
          !user?.accessToken ||
          user?.isSubscribing === true
        }
        onClick={() => {
          handlePaymentRequest();
        }}
        size={"lg"}
        color={"primary"}
        sx={{
          width: "100%",
          fontWeight: 700,
        }}
      >
        {buttonText}
      </Button>
    </>
  );
}
